import { Fragment } from 'react';
import PropTypes from 'prop-types';
/**
 * The Actions column's id. Both the search and saved-project column enums spell it
 * 'ACTIONS' (`PROFILER_SEARCH_RESULTS_COLUMNS.ACTIONS`,
 * `PROFILER_PROJECT_VIEW_COLUMNS.ACTIONS`), so this component does not need to know
 * which surface it is rendering for.
 */
export const PROFILER_ACTIONS_COLUMN_ID = 'ACTIONS';

/**
 * The Profiler list's column-header row (`.cols.profiler-search-cols`).
 *
 * TD-2720 — lifted out of `renderProfilerResultsHeader` in SearchResults/index.jsx.
 * The heatmap view is about to render the list *underneath* the heatmap, so this
 * row can no longer live inside the sticky `.results-header`: it has to sit
 * directly above the rows, wherever those start. It carries its own
 * `position: sticky` (see `.cols.profiler-search-cols.detached` in
 * search-results.less) so the labels still park under the toolbar while the list
 * scrolls. The blue rule closing the sticky stack stays on `.results-header`: see
 * the note in that stylesheet for why touching its box is not safe.
 *
 * TD-2733 — moved out of the search page, and `isColumnVisible` became a prop with the
 * move. Which columns the grid template must contain is not `column.selected` on every
 * surface: saved projects drop a fixed set of wide columns at 1201-1535px when extended
 * financials are on, regardless of selection. The predicate has to match the row
 * component's filter exactly or the header and the rows disagree on their track count,
 * so it belongs to the caller that owns both.
 *
 * `columnHeaderJSX` is the caller's slot map of per-column header nodes. Each node
 * carries its own <ColumnFilterTrigger/> (an observer reading the results store),
 * so they are passed in as ready-made nodes rather than rebuilt here — the
 * slot-node approach TD-2575 used for the Projects toolbar. That also keeps this
 * component free of store reads, so it needs no `observer` wrapper.
 */
const ProfilerColumnHeaderRow = ({
  profilerColumns,
  columnHeaderJSX,
  isColumnVisible,
  gridTemplateColumns
}) => {
  const actionsColumn = profilerColumns.find((column) => column.id === PROFILER_ACTIONS_COLUMN_ID);

  return (
    <div
      className="cols profiler-search-cols detached"
      style={{
        /*
         * HEADER GRID TEMPLATE CALCULATION
         * =================================
         * Calculates the CSS grid template for table headers based on selected columns.
         * Must match the row filtering logic in CompanyResult.jsx for proper alignment.
         */
        /*
         * TD-2733 — a caller may supply the template instead, when its rows build theirs
         * from the same source and the two must resolve identically (saved projects pin
         * their `fr` tracks with minmax so header and cell content cannot pull them apart).
         */
        gridTemplateColumns:
          gridTemplateColumns ||
          profilerColumns
            .filter(isColumnVisible)
            .map((column) => column.fragment)
            .join(' ')
      }}
    >
      {/*
       * HEADER CONTENT FILTERING
       * ========================
       * Filters and displays column headers based on user selection.
       * Must match the row filtering logic in CompanyResult.jsx.
       * Excludes the Actions column from header display.
       */}
      {profilerColumns
        .filter((column) => isColumnVisible(column) && column.id !== PROFILER_ACTIONS_COLUMN_ID)
        .map((column) => (
          <Fragment key={column.id}>{columnHeaderJSX[column.id]}</Fragment>
        ))}
      <div
        className="col actions profiler-search-actions"
        style={{ minWidth: actionsColumn?.minWidth }}
      ></div>
    </div>
  );
};

ProfilerColumnHeaderRow.propTypes = {
  profilerColumns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      selected: PropTypes.bool,
      fragment: PropTypes.string,
      minWidth: PropTypes.string
    })
  ).isRequired,
  columnHeaderJSX: PropTypes.objectOf(PropTypes.node).isRequired,
  isColumnVisible: PropTypes.func.isRequired,
  gridTemplateColumns: PropTypes.string
};

export default ProfilerColumnHeaderRow;
